import * as Sentry from '@sentry/react';
import { onlineManager, useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import _ from 'lodash';
import { useMemo } from 'react';
import { z } from 'zod';

import { useIsAuthenticated } from '../auth/authentication';
import { errorToast, successToast } from '../toast';
import { reportNetworkError } from '../utils/tracking';
import { useAxiosClient } from './axiosClient';
import {
    AxiosErrorContext,
    extractAxiosErrorContext,
    formatAxiosErrorSummary,
} from './axiosErrorContext';
import { fakeBeingOnline, queryClient } from './queryClient';

const DEFAULT_QUERY_ERROR_MESSAGE = 'Could not load some data.';
export const DEFAULT_MUTATION_SUCCESS_MESSAGE = 'Success!';
export const DEFAULT_MUTATION_ERROR_MESSAGE = 'Could not perform that action.';

const isOnline = () => fakeBeingOnline || onlineManager.isOnline();
const offlineErrorMessage = 'You are offline. Please connect to the internet to continue.';

// This function checks an error object returned by our Axios function call, and sees
//  if we got a 403 or 404 indicating that the user is not authorized to access a certain
//  endpoint. This allows queries and mutations to customise what happens when such a
//  response is received. For example, if you load a visit details page that gets a 404
//  or 403 response from the backend, the frontend will redirect you to the visits listing
//  page and tell you that you cannot see that visit.
// Why do we include 404 and not just 403? The backend tries to not let users know a resource
//  even exists if they do not have access to it, so if an object that does exist, but the user
//  cannot see it, the backend returns a 404 rather than a 403.
const isUnauthorizedError = (error: AxiosError) =>
    error.response?.status === 403 || error.response?.status === 404;

// To avoid logging validation errors to Sentry (i.e., InvalidInputError on the backend),
//  we check if the error is a 400 and if it has a message key.
const isValidationError = (error: AxiosError) =>
    error.response?.status === 400 && _.get(error, 'response.data.message', '').length > 0;

// If this error is thrown while fetching a query, then the closest <AxleSuspense/> component
//  will catch it and handle it appropriately. Specifically, the component tree will be kept
//  in a suspended-looking state (since we don’t know what to display), and the error will be
//  logged to Sentry if `shouldLogToSentry` is true.
export class AxleQueryFailedError extends Error {
    // Normal Error properties:
    name: string;
    message: string;
    stack?: string;

    // Custom properties.
    shouldLogToSentry: boolean;
    originalStack?: string;
    wrappedError?: Error;
    // A flat, JSON-serializable summary of the wrapped AxiosError. This is attached as
    //  structured Sentry context / Datadog attributes at the capture site (see suspense.tsx),
    //  so the valuable network-error details (url, code, timeout, duration, status, response)
    //  survive instead of being swallowed.
    axiosContext?: AxiosErrorContext;

    constructor({
        message,
        shouldLogToSentry,
        error,
    }: {
        message: string;
        shouldLogToSentry: boolean;
        error: Error | AxiosError | null;
    }) {
        const axiosContext = extractAxiosErrorContext(error);
        if (error) {
            // Append a compact, informative summary to this error's message so that even the
            //  Sentry/Datadog event *title* is useful (instead of `[object Object]`). The full
            //  structured detail is carried on `axiosContext` and reported at the capture site.
            if (axiosContext?.isAxiosError) {
                message += ` | ${formatAxiosErrorSummary(axiosContext)}`;
            } else {
                message += ` | ${error.name}: ${error.message}`;
            }
        }

        super(message); // This will set the `message` and `name` property.
        this.name = this.constructor.name; // The `name` is set incorrectly, so we reset it here.
        this.message = message; // Technically not needed, but helps with typing.
        this.shouldLogToSentry = shouldLogToSentry; // Store this for later reference by an error boundary.
        this.axiosContext = axiosContext;

        // Append the wrapped error's stack trace to this error's stack trace.
        if (error) {
            this.originalStack = this.stack;
            this.wrappedError = error;
            const numLinesForMessageInOriginalStackTrace =
                (this.message.match(/\n/g) ?? []).length + 1;
            this.stack =
                (this.stack ?? '')
                    .split('\n')
                    .slice(0, numLinesForMessageInOriginalStackTrace + 1)
                    .join('\n') +
                '\n' +
                (error.stack ?? '');
        }
    }
}

type AllowedQueryKey = readonly unknown[];
type AllowedHttpMethod = 'get' | 'post' | 'put' | 'delete' | 'patch';
export type AllowedQueryParams = Record<string, string | number | string[]>;

export const useAxleQuery = <
    Suspense extends boolean,
    ResponseSchemaOutput,
    ResponseSchemaInput = ResponseSchemaOutput,
    TransformedData = ResponseSchemaOutput,
>({
    queryKey,
    method = 'get',
    path,
    params = {},
    keepPreviousData = false,
    responseSchema = z.unknown() as z.Schema<
        ResponseSchemaOutput,
        z.ZodTypeDef,
        ResponseSchemaInput
    >,
    transform = (data) => data as unknown as TransformedData,
    placeholderData,
    defaultErrorMessage = DEFAULT_QUERY_ERROR_MESSAGE,
    suspense,
    deleteCacheAfterUnmount = false,
    onUnauthorized,
    // If you don't want Axios to be involved at all, but still want to use `useAxleQuery` with data
    //  that you provide, you can use this parameter.
    fakeRemoteData,
    enabled: enabledRaw = true,
    refetchInterval,
    refetchOnWindowFocus,
    noAuthenticationNeeded = false,
}: {
    queryKey?: AllowedQueryKey | undefined;
    method?: AllowedHttpMethod;
    path: string;
    params?: AllowedQueryParams;
    keepPreviousData?: boolean;
    responseSchema?: z.Schema<ResponseSchemaOutput, z.ZodTypeDef, ResponseSchemaInput>;
    transform?: (data: ResponseSchemaOutput) => TransformedData;
    // This is useful if you want to have data upon initial render,
    //  before the first query has completed, so the component doesn't suspend, but
    //  instead uses the placeholder data.
    placeholderData?: ResponseSchemaOutput;
    defaultErrorMessage?: string;
    // For complicated typing reasons, we need to ask callers to explicitly
    //  provide the value of `suspense` if they want the return type to be narrowed
    //  to not include `undefined`.
    suspense: Suspense;
    deleteCacheAfterUnmount?: boolean;
    onUnauthorized?: () => void;
    fakeRemoteData?: ResponseSchemaInput | undefined;
    enabled?: boolean;
    refetchInterval?: number;
    // refetchOnWindow focus can be the string literal "always"
    refetchOnWindowFocus?: boolean | string;
    noAuthenticationNeeded?: boolean;
}): {
    data: Suspense extends true ? TransformedData : TransformedData | undefined;
    isReady: boolean;
    refetch: () => Promise<unknown>;
} => {
    const axiosClient = useAxiosClient();

    queryKey ??= [method, path, params];
    // If fakeRemoteData is provided, include it in the query key, so if the fakeRemoteData
    //  value changes, the query function is rerun.
    if (fakeRemoteData !== undefined) {
        queryKey = [...queryKey, { fakeRemoteData }];
    }

    // Q: Why do we want parse and transform the data inside `queryFn`?
    //  Why do we do it later, outside of the `queryFn` we pass to React Query?
    //
    // A: The reason is we want the behavior where if the server response data
    //  is the same, then we do not cause a re-render. This is especially useful,
    //  e.g., in the en-route QHP live locations functionality, where we want it to
    //  refresh every 30 seconds, but if there's no update, we don't want to cause
    //  a re-render, as re-renders cause any popover components to lose their anchor
    //  element.
    //
    // React usually only works with referential equality, but React Query actually
    //  has a special feature called "structural sharing" where, only for JSON-compatible
    //  objects, it will do a deep comparison and maintain referential equality if the
    //  data is the same.
    //
    // If `queryFn` just returns the JSON data we got from the server, then this feature
    //  works as intended. But if we parse the data inside `queryFn`, then if there is,
    //  for example, a date field, because our Zod schemas convert the date strings to
    //  Moment objects, which are not JSON-compatible, and are a new instance every time,
    //  structural sharing breaks, and React Query thinks it's new and different data,
    //  and we get a re-render even though we didn't need it.
    //
    // To avoid all this, we just return the raw JSON data from the server in `queryFn`.
    //
    const queryFn = async ({ signal }: { signal: AbortSignal }): Promise<ResponseSchemaInput> => {
        // If we’re offline, show a toast to the user, and abort the query.
        if (!isOnline()) {
            errorToast(offlineErrorMessage);
            throw new AxleQueryFailedError({
                message: offlineErrorMessage,
                shouldLogToSentry: false,
                error: null,
            });
        }

        // Fake remote data lets the caller bypass using Axios altogether,
        //  and instead use whatever data they provide.
        if (fakeRemoteData !== undefined) {
            return fakeRemoteData;
        }

        // Issue the query, and parse the response with zod.
        try {
            const response = await axiosClient({
                method,
                url: path,
                params,
                // This lets React Query cancel the query if the component unmounts,
                //  or if the query becomes disabled. In particular, for queries
                //  requiring authentication, this makes Axios cancel the requests,
                //  which helps avoid spurious "AxiosError: Network Error" errors.
                signal,
            });

            // We don't know if the response data actually matches `ResponseSchemaInput`,
            //  but not to worry: it will be parsed with Zod later below.
            return response.data as ResponseSchemaInput;
        } catch (untypedError) {
            // Handle errors.
            //
            // If we get an error, we run any custom unauthorised response logic
            //  if applicable. Else, standard error handling is to show a toast to
            //  the user and leave any components using the query in a suspended
            //  looking state.
            //
            // If multiple components use the same query, and the query fails, this onError
            //  callback is called many times (see: https://tkdodo.eu/blog/react-query-error-handling#the-onerror-callback).
            //  Luckily, our toasting helper is smart enough to deduplicate toasts on the screen.

            const error = untypedError as AxiosError;

            // If the query was canceled (say because a component was unmounted,
            //  or the user logged out), then neither show the user a toast,
            //  nor log it to Sentry.
            if (error.code === 'ERR_CANCELED') {
                throw new AxleQueryFailedError({
                    message: 'Query was canceled.',
                    shouldLogToSentry: false,
                    error: error,
                });
            }

            // Allow the caller to customise what happens when we get an authorisation error.
            //  This is used, for example, by the visit detail page, where it is expected
            //  a supply-side admin might lose access to a visit if their org is no longer
            //  assigned a visit.
            if (isUnauthorizedError(error) && onUnauthorized) {
                onUnauthorized();
                throw new AxleQueryFailedError({
                    message: 'Custom onUnauthorized() handler was called',
                    shouldLogToSentry: false,
                    error: error,
                });
            } else if (isUnauthorizedError(error)) {
                // Otherwise, if any API request returns a 403 "Unauthorized" error, we should
                //  log this to Sentry. It’s surprising that we should ever get it; our flow should
                //  have prevented such a request from ever being made.
                Sentry.captureMessage('Got a 403, which is unexpected.');
                reportNetworkError(error, extractAxiosErrorContext(error));
            }

            // Show a toast to let the user know something went wrong.
            //  The back-end can customise the error message by including a `message` field
            //  in the JSON response, otherwise we have a default error message.
            //  To suppress the error message entirely, callers can set `defaultErrorMessage` to ''.
            const errorMessage = _.get(error, 'response.data.message', defaultErrorMessage);
            if (errorMessage) {
                errorToast(errorMessage);
            }

            // Instead of throwing the original error, we throw an AxleQueryFailedError()
            //  that will get caught by the nearest <AxleSuspense/> component keep the
            //  component tree in a suspended-looking state.
            throw new AxleQueryFailedError({
                message: errorMessage,
                // Only log to Sentry if it's not a validation error.
                shouldLogToSentry: !isValidationError(error),
                error: error,
            });
        }
    };

    const isAuthenticated = useIsAuthenticated();
    const enabled = enabledRaw && (noAuthenticationNeeded || isAuthenticated);

    const {
        data: rawData,
        error,
        isSuccess,
        refetch,
    } = useQuery<ResponseSchemaInput, Error, ResponseSchemaInput>({
        // @ts-expect-error TODO: Figure out why typing for options is not working.
        queryKey,
        queryFn,
        keepPreviousData,
        suspense,
        placeholderData: placeholderData,
        cacheTime: deleteCacheAfterUnmount ? 0 : undefined,
        enabled,
        refetchInterval,
        refetchOnWindowFocus,
    });

    // Usually, if we are using suspense, then if there is an error in the query
    //  function, `useQuery` will already have thrown the error, and we wouldn't be
    //  here. However, if `suspense` is false, or `keepPreviousData` is true, then
    //  we might end up having an error here. In that case, we throw the error
    //  so our error boundaries will catch it, since we never want to return
    //  invalid data to our components.
    if (error) {
        throw error;
    }

    // We wrap the parsing/transforming of the data in `useMemo()` to maintain
    //  referential equality of the parsed/transformed object, if the underlying
    //  raw data stayed the same.
    const data = useMemo(() => {
        if (rawData === undefined) {
            if (suspense) {
                // When `suspense` is false, then it's possible that `rawData` is undefined,
                //  but, if `suspense` is true, `rawData` should always be defined and contain
                //  the query function data. Something has gone very unexpectedly wrong.
                throw new AxleQueryFailedError({
                    message: "Suspense is true, but response is undefined. This shouldn't happen.",
                    shouldLogToSentry: true,
                    error: null,
                });
            } else {
                return undefined;
            }
        } else {
            try {
                return transform(responseSchema.parse(rawData));
            } catch (err) {
                errorToast(DEFAULT_QUERY_ERROR_MESSAGE);
                throw new AxleQueryFailedError({
                    message: 'Could not parse/transform response data.',
                    shouldLogToSentry: true,
                    error: err as Error,
                });
            }
        }
        // ESLint wants us to include `responseSchema`, `suspense`, and `transform` too,
        //  but the thing is because `useAxleQuery()` callers usually defined `transform`
        //  as an arrow function that is a different instance every time, including them
        //  in the dependency array would defeat the point of the `useMemo()` by
        //  re-calculating every time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawData]);

    return {
        // If `Suspense` is true, then the data is always defined; otherwise, it can be undefined.
        data: data as Suspense extends true ? TransformedData : TransformedData | undefined,
        isReady: isSuccess,
        refetch,
    };
};

const softReset = (queryKeys: AllowedQueryKey[]) =>
    Promise.all(queryKeys.map((queryKey) => queryClient.invalidateQueries(queryKey)));
const hardReset = (queryKeys: AllowedQueryKey[]) =>
    Promise.all(queryKeys.map((queryKey) => queryClient.resetQueries(queryKey)));

export const useAxleMutation = <
    MutationFnArgs,
    ResponseSchemaOutput,
    ResponseSchemaInput = ResponseSchemaOutput,
    TransformedData = ResponseSchemaOutput,
>({
    axiosConfig,
    responseSchema = z.unknown() as z.Schema<
        ResponseSchemaOutput,
        z.ZodTypeDef,
        ResponseSchemaInput
    >,
    transform = (data) => data as unknown as TransformedData,
    // This marks the query data as stale, and starts fetching new data in the background.
    //  Any already mounted components won’t be suspended; they’ll show their stale data
    //  until new data comes in. If new components are mounted, they’ll also show the
    //  stale data until the new data comes in. This makes for a very pleasant, “soft”
    //  refreshing experience.
    // This is the right default choice for most cases.
    softResetQueries = [],
    // This makes components suspend until new data is fetched. If the components were
    //  already mounted, they will suspend. This is useful for when you want to make
    //  sure stale data does not remain on the screen.
    // This is sometimes appropriate.
    hardResetQueries = [],
    // You can set this to an empty string to suppress the default success message.
    defaultSuccessMessage = DEFAULT_MUTATION_SUCCESS_MESSAGE,
    // You can set this to an empty string to suppress the default error message.
    defaultErrorMessage = DEFAULT_MUTATION_ERROR_MESSAGE,
    // You should leave this to false unless you are okay with duplicate toasts.
    allowDuplicateToasts = false,
}: {
    axiosConfig: (args: MutationFnArgs) => {
        method: AllowedHttpMethod;
        path: string;
        data?: unknown;
        // Note that these are called before any queries have been hard-reset.
        //  TODO: Make it so these are only called after the queries have been hard-reset.
        onSuccess?: (data: TransformedData) => void;
        onError?: () => void;
        onSettled?: () => void;
    };
    responseSchema?: z.Schema<ResponseSchemaOutput, z.ZodTypeDef, ResponseSchemaInput>;
    transform?: (data: ResponseSchemaOutput) => TransformedData;
    softResetQueries?: AllowedQueryKey[];
    hardResetQueries?: AllowedQueryKey[];
    defaultSuccessMessage?: string;
    defaultErrorMessage?: string;
    allowDuplicateToasts?: boolean;
}) => {
    const axiosClient = useAxiosClient();

    const mutationFn = async (args: MutationFnArgs): Promise<TransformedData> => {
        // If we’re offline, return a response as if it came from Axios that
        //  makes the error handling code show the user a toast telling
        //  them the network is down.
        if (!isOnline()) {
            errorToast(offlineErrorMessage);
            throw new Error(offlineErrorMessage);
        }

        const {
            method,
            path,
            data: requestData,
            onSuccess: onSuccessCaller,
            onSettled: onSettledCaller,
            onError: onErrorCaller,
        } = axiosConfig(args);

        const onSettled = () => {
            onSettledCaller?.();

            // We always soft-reset any queries to be soft-reset, regardless of whether the
            //  mutation succeeded or errored out.
            void softReset(softResetQueries);
        };

        let response: AxiosResponse<unknown>;
        try {
            response = await axiosClient({
                method,
                url: path,
                data: requestData,
            });
        } catch (untypedError) {
            const error = untypedError as AxiosError;

            // If a mutation errors out, we show the error message immediately.
            // We also dispatch any hard-reset queries to refetch in the background,
            //  but this isn’t strictly necessary, as no data should have changed.
            void softReset(hardResetQueries);

            const errorMessage = _.get(error, 'response.data.message', defaultErrorMessage);
            if (errorMessage) {
                errorToast(errorMessage, { deduplicate: !allowDuplicateToasts });
            }

            // Report the failure to Sentry/Datadog with structured network context. Mutations
            //  previously threw the raw AxiosError and reported nothing, so backend failures
            //  during a save (timeouts, 502s) were invisible to observability. We skip canceled
            //  requests (400) errors, mirroring the query path.
            if (error.code !== 'ERR_CANCELED') {
                reportNetworkError(error, extractAxiosErrorContext(error));
            }

            onErrorCaller?.();
            onSettled();

            throw error;
        }

        const responseData = transform(responseSchema.parse(response.data));

        // If a mutation was successful, we first hard-reset any queries to be hard-reset,
        //  and only then show the success toast. This leads to a smoother experience.
        //  Otherwise, users see a success toast, but the UX is still unusable until
        //  the hard-reset queries finish.
        void hardReset(hardResetQueries).then(() => {
            const successMessage = _.get(response.data, 'message', defaultSuccessMessage);
            if (successMessage) {
                successToast(successMessage, { deduplicate: !allowDuplicateToasts });
            }
        });

        onSuccessCaller?.(responseData);
        onSettled();

        return responseData;
    };

    const mutation = useMutation({
        mutationFn,
    });

    return mutation.mutateAsync;
};

// When the data for a mutation includes a file, you need to wrap it with this.
//  What I think this does is makes Axios use multipart/form-data instead of
//  application/json, which makes the file upload work.
// The allowed values are strings and Blobs (i.e., files).
export const dataWithFiles = (data: Record<string, string | Blob>): FormData => {
    const formData = new FormData();
    Object.keys(data).forEach((key) => formData.append(key, data[key]));
    return formData;
};
